import React from "react";

type FrameVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

type AnimationVideoProps = {
  src: string;
  poster: string;
  className?: string;
  loop?: boolean;
  onEnded?: React.ReactEventHandler<HTMLVideoElement>;
  onError?: React.ReactEventHandler<HTMLVideoElement>;
};

/** 再生前のネイティブUIを隠し、最初のフレームが描画されてから動画を見せる。 */
export const AnimationVideo: React.FC<AnimationVideoProps> = ({ src, poster, className, loop, onEnded, onError }) => {
  const ref = React.useRef<FrameVideo>(null);
  const frame = React.useRef<number>();
  const [readySource, setReadySource] = React.useState<string | null>(null);
  const ready = readySource === src;

  React.useEffect(() => {
    const video = ref.current;
    setReadySource(null);
    // WebKitのautoplayは非表示の動画を開始しない場合がある。mutedのplay()は非表示でも許可される。
    // 拒否された場合も静止画とページのボタンは使える。
    video?.play()?.catch(() => undefined);
    return () => {
      if (frame.current !== undefined) video?.cancelVideoFrameCallback?.(frame.current);
      frame.current = undefined;
    };
  }, [src]);

  const handlePlaying: React.ReactEventHandler<HTMLVideoElement> = (event) => {
    const video = event.currentTarget as FrameVideo;
    if (frame.current !== undefined) video.cancelVideoFrameCallback?.(frame.current);
    const reveal = () => {
      frame.current = undefined;
      if (ref.current === video) setReadySource(src);
    };
    if (video.requestVideoFrameCallback) frame.current = video.requestVideoFrameCallback(reveal);
    else reveal();
  };

  const cover = () => {
    const video = ref.current;
    if (frame.current !== undefined) video?.cancelVideoFrameCallback?.(frame.current);
    frame.current = undefined;
    setReadySource(null);
  };

  return (
    <div className={className} style={{ position: "relative", aspectRatio: "1 / 1" }}>
      <video
        key={src}
        ref={ref}
        src={src}
        poster={poster}
        width={720}
        height={720}
        autoPlay
        playsInline
        muted
        loop={loop}
        preload="auto"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: ready ? 1 : 0,
          pointerEvents: "none",
        }}
        onPlaying={handlePlaying}
        onWaiting={cover}
        onEnded={onEnded}
        onError={(event) => {
          cover();
          onError?.(event);
        }}
      />
      {!ready && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          width={720}
          height={720}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
};
